import { db, storage } from '../config/firebase';
import { collection, addDoc, updateDoc, doc, getDoc, getDocs, query, where, orderBy, serverTimestamp, onSnapshot } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

class ChatService {
  // Create a new chat conversation - with duplicate prevention
  async createChat(chatData) {
    try {
      console.log('🔄 createChat called with data:', chatData);
      
      // First, check if a chat already exists for this client-lawyer combination
      if (chatData.clientId && chatData.lawyerId) {
        console.log('🔍 Checking for existing chat between client:', chatData.clientId, 'and lawyer:', chatData.lawyerId);
        
        const existingChatsQuery = query(
          collection(db, 'chats'),
          where("clientId", "==", chatData.clientId),
          where("lawyerId", "==", chatData.lawyerId),
          where("status", "in", ["active", "pending"])
        );
        
        const existingChatsSnapshot = await getDocs(existingChatsQuery);
        
        if (!existingChatsSnapshot.empty) {
          // Chat already exists, return the existing chat ID
          const existingChat = existingChatsSnapshot.docs[0];
          console.log('🔄 Chat already exists, returning existing chat ID:', existingChat.id);
          return { 
            success: true, 
            chatId: existingChat.id, 
            isExisting: true,
            data: { ...existingChat.data(), chatId: existingChat.id }
          };
        }
      }
      
      // No existing chat found, create a new one
      const chat = {
        ...chatData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        lastMessage: '',
        lastMessageTime: serverTimestamp(),
        unreadCount: 0
      };

      console.log('🔄 Final chat object to save:', chat);
      const docRef = await addDoc(collection(db, 'chats'), chat);
      console.log('✅ Chat document created with ID:', docRef.id);
      
      // Update the chat with its ID
      await updateDoc(docRef, { chatId: docRef.id });
      console.log('✅ Chat document updated with chatId field');
      
      const result = {
        success: true,
        chatId: docRef.id,
        isExisting: false,
        data: { ...chat, chatId: docRef.id }
      };
      
      console.log('✅ createChat returning:', result);
      return result;
    } catch (error) {
      console.error('❌ Error creating chat:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Send a message with support for text, files, and voice
  async sendMessage(chatId, messageData, file = null, audioBlob = null) {
    try {
      // First, check if the chat/booking exists
      const chatRef = doc(db, 'chats', chatId);
      const chatDoc = await getDoc(chatRef);
      
      if (!chatDoc.exists()) {
        return {
          success: false,
          error: 'Chat not found'
        };
      }
      
      const chatData = chatDoc.data();
      
      // For lawyer messages, allow sending regardless of status
      // For client messages, check if the booking status is 'active'
      if (messageData.sender !== 'lawyer' && chatData.status !== 'active') {
        return {
          success: false,
          error: 'Chat is not active. Please wait for the lawyer to accept your request.'
        };
      }
      
      // Auto-activate chat if lawyer is sending a message and chat is pending
      if (messageData.sender === 'lawyer' && chatData.status === 'pending') {
        console.log('🔄 Auto-activating pending chat for lawyer message');
        try {
          await updateDoc(chatRef, {
            status: 'active',
            updatedAt: serverTimestamp()
          });
          console.log('✅ Chat auto-activated successfully');
        } catch (activateError) {
          console.error('❌ Error auto-activating chat:', activateError);
        }
      }
      
      let fileUrl = null;
      let fileType = null;
      let messageType = messageData.type || 'text';

      // Handle file upload
      if (file) {
        const fileRef = ref(storage, `chats/${chatId}/files/${Date.now()}-${file.name}`);
        await uploadBytes(fileRef, file);
        fileUrl = await getDownloadURL(fileRef);
        fileType = file.type;
        messageType = file.type.startsWith('image/') ? 'image' : 'file';
      }

      // Handle audio upload
      if (audioBlob) {
        const audioRef = ref(storage, `chats/${chatId}/voice/${Date.now()}.webm`);
        await uploadBytes(audioRef, audioBlob, { contentType: "audio/webm" });
        fileUrl = await getDownloadURL(audioRef);
        fileType = "audio/webm";
        messageType = 'voice';
      }

      const message = {
        ...messageData,
        chatId,
        type: messageType,
        fileUrl,
        fileType,
        createdAt: serverTimestamp(),
        read: false
      };

      const docRef = await addDoc(collection(db, 'chats', chatId, 'messages'), message);
      
      // Update chat with last message info
      const chatUpdateRef = doc(db, 'chats', chatId);
      await updateDoc(chatUpdateRef, {
        lastMessage: messageData.content || messageData.text || (file ? 'File sent' : 'Voice message'),
        lastMessageTime: serverTimestamp(),
        updatedAt: serverTimestamp(),
        unreadCount: messageData.sender === 'lawyer' ? 1 : 0 // Increment unread count for lawyer messages
      });
      
      return {
        success: true,
        messageId: docRef.id,
        data: { ...message, messageId: docRef.id }
      };
    } catch (error) {
      console.error('Error sending message:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Send a message with legacy format support
  async sendMessageLegacy(chatId, senderId, messageText, messageType = 'text', fileUrl = null) {
    try {
      const message = {
        chatId,
        sender_id: senderId,
        message_text: messageText,
        message_type: messageType,
        file_url: fileUrl,
        createdAt: serverTimestamp(),
        read: false
      };

      const docRef = await addDoc(collection(db, 'chats', chatId, 'messages'), message);
      
      // Update chat with last message info
      const chatRef = doc(db, 'chats', chatId);
      await updateDoc(chatRef, {
        lastMessage: messageText,
        lastMessageTime: serverTimestamp(),
        updatedAt: serverTimestamp(),
        unreadCount: senderId !== 'user' ? 1 : 0 // Increment unread count for lawyer messages
      });
      
      const result = {
        success: true,
        messageId: docRef.id,
        data: { ...message, messageId: docRef.id }
      };
      
      // Dispatch event to update chat list
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('chatUpdated'));
      }
      
      return result;
    } catch (error) {
      console.error('Error sending message:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Get chat messages
  async getChatMessages(chatId) {
    try {
      const q = query(
        collection(db, 'chats', chatId, 'messages'),
        orderBy('createdAt', 'asc')
      );
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return {
          success: true,
          data: []
        };
      }
      
      const messages = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      
      return {
        success: true,
        data: messages
      };
    } catch (error) {
      console.error('Error getting chat messages:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Subscribe to chat messages in real-time
  async subscribeToMessages(chatId, callback) {
    try {
      console.log('🔍 Setting up message subscription for chat:', chatId);
      
      const { db } = await import('../config/firebase');
      const { collection, query, onSnapshot } = await import('firebase/firestore');
      
      // Use a simple query without ordering to avoid index requirements
      const q = query(
        collection(db, 'chats', chatId, 'messages')
      );

      return onSnapshot(q, (snapshot) => {
        console.log('📨 Message snapshot received:', snapshot.size, 'messages');
        
        if (snapshot.empty) {
          console.log('📭 No messages found in chat');
          callback([]); // ✅ stops infinite "loading..."
          return;
        }

        const msgs = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Sort messages by createdAt locally to avoid Firestore index issues
        msgs.sort((a, b) => {
          const timeA = a.createdAt?.toDate?.() || new Date(a.createdAt) || new Date(0);
          const timeB = b.createdAt?.toDate?.() || new Date(b.createdAt) || new Date(0);
          return timeA - timeB; // Ascending order (oldest first)
        });

        console.log('✅ Messages loaded and sorted:', msgs.length);
        callback(msgs);
      }, (error) => {
        console.error('❌ Error in onSnapshot for chat messages:', error);
        callback([]); // Fallback to empty array
      });
    } catch (error) {
      console.error('❌ Error setting up chat messages subscription:', error);
      callback([]); // Fallback to empty array
    }
  }

  // Update typing status
  async updateTypingStatus(chatId, userId, isTyping) {
    try {
      const chatRef = doc(db, 'chats', chatId);
      await updateDoc(chatRef, {
        [`typing.${userId}`]: isTyping,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating typing status:', error);
    }
  }

  // Subscribe to typing status
  async subscribeToTypingStatus(chatId, callback) {
    try {
      const { db } = await import('../config/firebase');
      const { doc, onSnapshot } = await import('firebase/firestore');
      
      const chatRef = doc(db, 'chats', chatId);
      
      return onSnapshot(chatRef, (doc) => {
        if (doc.exists()) {
          const data = doc.data();
          callback(data.typing || {});
        } else {
          callback({});
        }
      });
    } catch (error) {
      console.error('Error subscribing to typing status:', error);
      callback({});
    }
  }

  // Subscribe to chat status changes
  async subscribeToChatStatus(chatId, callback) {
    try {
      console.log('📊 Setting up chat status subscription for chat:', chatId);
      
      const { db } = await import('../config/firebase');
      const { doc, onSnapshot } = await import('firebase/firestore');
      
      const chatRef = doc(db, 'chats', chatId);
      
      return onSnapshot(chatRef, (doc) => {
        if (doc.exists()) {
          const chatData = doc.data();
          console.log('📊 Chat status update received:', chatData);
          callback(chatData);
        }
      });
    } catch (error) {
      console.error('❌ Error setting up chat status subscription:', error);
      return () => {}; // Return empty cleanup function
    }
  }

  // Get user's chats
  async getUserChats(userId) {
    try {
      // Check if userId is valid
      if (!userId) {
        console.error('getUserChats: userId is undefined or null');
        return {
          success: false,
          error: 'User ID is required',
          data: []
        };
      }

      // Only get chats for accepted bookings (status = 'active')
      const q = query(
        collection(db, 'chats'),
        where('clientId', '==', userId),
        where('status', '==', 'active'),
        orderBy('lastMessageTime', 'desc')
      );
      const querySnapshot = await getDocs(q);
      
      const chats = [];
      querySnapshot.forEach((doc) => {
        chats.push({ id: doc.id, ...doc.data() });
      });
      
      return {
        success: true,
        data: chats
      };
    } catch (error) {
      console.error('Error getting user chats:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Mark messages as read
  async markMessagesAsRead(chatId, userId) {
    try {
      const q = query(
        collection(db, 'chats', chatId, 'messages'),
        where('sender', '!=', userId),
        where('read', '==', false)
      );
      const querySnapshot = await getDocs(q);
      
      const updatePromises = querySnapshot.docs.map(doc => 
        updateDoc(doc.ref, { read: true })
      );
      
      await Promise.all(updatePromises);
      
      // Reset unread count
      const chatRef = doc(db, 'chats', chatId);
      await updateDoc(chatRef, { unreadCount: 0 });
      
      return {
        success: true,
        message: 'Messages marked as read'
      };
    } catch (error) {
      console.error('Error marking messages as read:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Listen to chat messages in real-time
  listenToChatMessages(chatId, callback) {
    try {
      const q = query(
        collection(db, 'messages'),
        where('chatId', '==', chatId)
      );
      
      return onSnapshot(q, (snapshot) => {
        const messages = [];
        snapshot.forEach((doc) => {
          messages.push({ id: doc.id, ...doc.data() });
        });
        // Sort messages by created_at timestamp
        messages.sort((a, b) => {
          const timeA = a.created_at?.toDate?.() || new Date(a.created_at) || new Date(0);
          const timeB = b.created_at?.toDate?.() || new Date(b.created_at) || new Date(0);
          return timeA - timeB;
        });
        callback(messages);
      });
    } catch (error) {
      console.error('Error listening to chat messages:', error);
      // Fallback: return empty array
      callback([]);
    }
  }

  // Listen to user's chats in real-time
  listenToUserChats(userId, callback) {
    try {
      const q = query(
        collection(db, 'chats'),
        where('participants', 'array-contains', userId)
      );
      
      return onSnapshot(q, (snapshot) => {
        const chats = [];
        snapshot.forEach((doc) => {
          chats.push({ id: doc.id, ...doc.data() });
        });
        // Sort chats by updatedAt timestamp
        chats.sort((a, b) => {
          const timeA = a.updatedAt?.toDate?.() || new Date(a.updatedAt) || new Date(0);
          const timeB = b.updatedAt?.toDate?.() || new Date(b.updatedAt) || new Date(0);
          return timeB - timeA; // Descending order
        });
        callback(chats);
      });
    } catch (error) {
      console.error('Error listening to user chats:', error);
      // Fallback: return empty array
      callback([]);
    }
  }

  // Send automated message
  async sendAutomatedMessage(chatId, messageType, bookingData = {}) {
    try {
      let content = '';
      
      switch (messageType) {
        case 'booking_confirmed':
          const consultationTypesText = bookingData.consultationTypes?.map(type => 
            type === 'call' ? 'Phone Call' : 
            type === 'video' ? 'Video Call' : 
            type === 'text' ? 'Text Consultation' : 
            type === 'emergency' ? 'Emergency Consultation' : 
            'In Person Consultation'
          ).join(', ') || 'General Consultation';
          
          const fileReadingText = bookingData.fileReadingCharges?.length > 0 ? 
            `\n📄 File Reading: ${bookingData.fileReadingCharges.map(c => `${c.fromPage}-${c.toPage} pages`).join(', ')}` : '';
          
          content = `🎉 **Booking Confirmed Successfully!**

📋 **Booking ID:** ${bookingData.bookingId || 'N/A'}
👨‍💼 **Lawyer:** ${bookingData.lawyerName}
📅 **Date:** ${bookingData.date}
⏰ **Time:** ${bookingData.time}
⏱️ **Duration:** ${bookingData.duration || 60} minutes
💼 **Services:** ${consultationTypesText}${fileReadingText}

💰 **Payment Details:**
• Consultation Fee: ₹${bookingData.consultationFees || 0}
• Service Charge: ₹${bookingData.serviceCharge || 200}
• **Total Amount:** ₹${bookingData.totalAmount || 0}

✅ Your booking is now pending lawyer approval. You'll be notified once the lawyer accepts your consultation request.

💬 You can start chatting with the lawyer to discuss your case details.`;
          break;
        case 'booking_accepted':
          content = `✅ Your booking has been accepted by the lawyer. You can now start chatting and prepare for your consultation.`;
          break;
        case 'booking_rejected':
          content = `❌ Your booking has been cancelled by the lawyer. You will receive a refund shortly.`;
          break;
        case 'consultation_completed':
          content = `✅ Your consultation has been completed. Thank you for using our service!`;
          break;
        default:
          content = 'System message';
      }

      const messageData = {
        content,
        sender: 'system',
        type: 'system',
        messageType
      };

      const result = await this.sendMessage(chatId, messageData);
      
      // Dispatch event to update chat list
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('chatUpdated'));
      }
      
      return result;
    } catch (error) {
      console.error('Error sending automated message:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Update existing chat with real profile data
  async updateChatWithRealProfiles(chatId) {
    try {
      console.log('🔄 Updating chat with real profiles:', chatId);
      
      // Get the chat data
      const chatRef = doc(db, 'chats', chatId);
      const chatDoc = await getDoc(chatRef);
      
      if (!chatDoc.exists()) {
        console.error('❌ Chat not found:', chatId);
        return { success: false, error: 'Chat not found' };
      }
      
      const chatData = chatDoc.data();
      console.log('📋 Current chat data:', chatData);
      
      // Fetch real profile data
      let clientProfile = null;
      let lawyerProfile = null;
      
      // Fetch client profile from users collection
      if (chatData.clientId) {
        const clientDoc = await getDoc(doc(db, 'users', chatData.clientId));
        if (clientDoc.exists()) {
          clientProfile = clientDoc.data();
          console.log('✅ Client profile fetched:', clientProfile);
        }
      }
      
      // Fetch lawyer profile from lawyers collection
      if (chatData.lawyerId) {
        const lawyerDoc = await getDoc(doc(db, 'lawyers', chatData.lawyerId));
        if (lawyerDoc.exists()) {
          lawyerProfile = lawyerDoc.data();
          console.log('✅ Lawyer profile fetched:', lawyerProfile);
        }
      }
      
      // Prepare updated data
      const updates = {};
      
      if (clientProfile) {
        const clientName = clientProfile.full_name || clientProfile.displayName || clientProfile.name || chatData.clientName;
        const clientEmail = clientProfile.email || chatData.clientEmail;
        const clientPhoto = clientProfile.profile_picture || clientProfile.profilePic || clientProfile.photoURL || chatData.clientPhoto;
        
        if (clientName !== chatData.clientName) updates.clientName = clientName;
        if (clientEmail !== chatData.clientEmail) updates.clientEmail = clientEmail;
        if (clientPhoto !== chatData.clientPhoto) updates.clientPhoto = clientPhoto;
      }
      
      if (lawyerProfile) {
        const lawyerName = lawyerProfile.full_name || lawyerProfile.displayName || lawyerProfile.name || chatData.lawyerName;
        const lawyerEmail = lawyerProfile.email || chatData.lawyerEmail;
        const lawyerPhoto = lawyerProfile.profile_picture || lawyerProfile.profilePic || lawyerProfile.photoURL || chatData.lawyerPhoto;
        const lawyerProfilePic = lawyerProfile.profile_picture || lawyerProfile.profilePic || lawyerProfile.photoURL || chatData.lawyerPhoto;
        const specialization = lawyerProfile.specialization?.join(', ') || lawyerProfile.specialization || chatData.specialization;
        
        if (lawyerName !== chatData.lawyerName) updates.lawyerName = lawyerName;
        if (lawyerEmail !== chatData.lawyerEmail) updates.lawyerEmail = lawyerEmail;
        if (lawyerPhoto !== chatData.lawyerPhoto) updates.lawyerPhoto = lawyerPhoto;
        if (lawyerProfilePic !== chatData.lawyerProfilePic) updates.lawyerProfilePic = lawyerProfilePic;
        if (specialization !== chatData.specialization) updates.specialization = specialization;
      }
      
      // Update the chat if there are changes
      if (Object.keys(updates).length > 0) {
        updates.updatedAt = serverTimestamp();
        await updateDoc(chatRef, updates);
        console.log('✅ Chat updated with real profiles:', updates);
        
        return {
          success: true,
          message: 'Chat updated with real profile data',
          updates
        };
      } else {
        console.log('ℹ️ No updates needed for chat');
        return {
          success: true,
          message: 'Chat already has correct profile data'
        };
      }
      
    } catch (error) {
      console.error('❌ Error updating chat with real profiles:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Create or get existing chat room between lawyer and client
  async createOrGetChatRoom(lawyerId, clientId) {
    try {
      if (!lawyerId || !clientId) {
        return {
          success: false,
          error: 'Lawyer ID and Client ID are required'
        };
      }

      console.log('🔍 Creating or getting chat room for lawyer:', lawyerId, 'and client:', clientId);

      // First, check if a chat already exists between this lawyer and client
      const existingChatQuery = query(
        collection(db, 'chats'),
        where('lawyerId', '==', lawyerId),
        where('clientId', '==', clientId)
      );

      const existingChatSnapshot = await getDocs(existingChatQuery);

      if (!existingChatSnapshot.empty) {
        // Chat already exists, return the first one
        const existingChat = existingChatSnapshot.docs[0];
        console.log('✅ Found existing chat:', existingChat.id);
        return {
          success: true,
          chatId: existingChat.id,
          data: { id: existingChat.id, ...existingChat.data() }
        };
      }

      // Create new chat room with only essential data
      const newChatData = {
        lawyerId: lawyerId,
        clientId: clientId,
        status: 'active', // Set to 'active' when lawyer accepts booking
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        lastMessage: '',
        lastMessageTime: serverTimestamp(),
        unreadCount: 0
      };

      console.log('📋 Creating new chat with data:', newChatData);

      const chatRef = await addDoc(collection(db, 'chats'), newChatData);
      
      // Update the chat with its ID
      await updateDoc(chatRef, { chatId: chatRef.id });
      
      console.log('✅ New chat room created:', chatRef.id);

      return {
        success: true,
        chatId: chatRef.id,
        data: { ...newChatData, chatId: chatRef.id }
      };
    } catch (error) {
      console.error('Error creating or getting chat room:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }
}

export const chatService = new ChatService();

// Export the subscription function for easy access
export const subscribeToMessages = (chatId, callback) => {
  return chatService.subscribeToMessages(chatId, callback);
}; 

// Get all chats for a specific user from database
export const getUserChats = async (userId) => {
  try {
    console.log('🔍 Getting user chats from database for user:', userId);
    
    // Check if userId is valid
    if (!userId) {
      console.error('getUserChats: userId is undefined or null');
      return {
        success: false,
        error: 'User ID is required',
        data: []
      };
    }
    
    const { db } = await import('../config/firebase');
    const { collection, query, where, getDocs, orderBy } = await import('firebase/firestore');
    
    // Query chats where user is a participant and status is active (accepted bookings only)
    const chatsRef = collection(db, 'chats');
    const q = query(
      chatsRef,
      where('clientId', '==', userId), // Client can only see their own chats
      where('status', '==', 'active'), // Only show chats for accepted bookings
      orderBy('lastMessageTime', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    const chats = [];
    
    querySnapshot.forEach((doc) => {
      const chatData = doc.data();
      chats.push({
        id: doc.id,
        ...chatData,
        // Ensure consistent field names
        lawyerName: chatData.lawyerName || chatData.lawyer?.name || 'Lawyer',
        lawyerProfilePic: chatData.lawyerProfilePic || chatData.lawyer?.profilePic || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
        specialization: chatData.specialization || chatData.lawyer?.specialization || 'General Law',
        lastMessage: chatData.lastMessage || '',
        lastMessageTime: chatData.lastMessageTime || chatData.updatedAt || new Date(),
        unreadCount: chatData.unreadCount || 0,
        status: chatData.status || 'active'
      });
    });
    
    console.log('🔍 Found', chats.length, 'chats in database');
    return { success: true, data: chats };
  } catch (error) {
    console.error('Error getting user chats:', error);
    return { success: false, error: error.message };
  }
};

// Sync local cache with database
export const syncChatsWithDatabase = async (userId) => {
  try {
    console.log('🔄 Syncing chats with database...');
    
    // Check if userId is valid
    if (!userId) {
      console.error('syncChatsWithDatabase: userId is undefined or null');
      return {
        success: false,
        error: 'User ID is required',
        data: []
      };
    }
    
    // Get chats from database
    const dbResult = await getUserChats(userId);
    
    if (dbResult.success) {
      // Update localStorage with database data
      localStorage.setItem('userChats', JSON.stringify(dbResult.data));
      console.log('✅ Synced', dbResult.data.length, 'chats to localStorage');
      return { success: true, data: dbResult.data };
    } else {
      console.log('❌ Failed to get chats from database, using local cache');
      // Fallback to local cache
      const localChats = JSON.parse(localStorage.getItem('userChats') || '[]');
      return { success: true, data: localChats };
    }
  } catch (error) {
    console.error('Error syncing chats:', error);
    // Fallback to local cache
    const localChats = JSON.parse(localStorage.getItem('userChats') || '[]');
    return { success: true, data: localChats };
  }
};

// LAWYER-SPECIFIC METHODS

// Get all chats for a lawyer from database
export const getLawyerChats = async (lawyerId) => {
  try {
    console.log('🔍 Getting lawyer chats from database for lawyer:', lawyerId);
    
    if (!lawyerId) {
      console.error('getLawyerChats: lawyerId is undefined or null');
      return {
        success: false,
        error: 'Lawyer ID is required',
        data: []
      };
    }
    
    const { db } = await import('../config/firebase');
    const { collection, query, where, getDocs, orderBy } = await import('firebase/firestore');
    
    // Query chats where lawyer is the lawyerId - include both active and pending status
    const chatsRef = collection(db, 'chats');
    const q = query(
      chatsRef,
      where('lawyerId', '==', lawyerId),
      where('status', 'in', ['active', 'pending']), // Show both active and pending chats
      orderBy('lastMessageTime', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    const chats = [];
    
    querySnapshot.forEach((doc) => {
      const chatData = doc.data();
      chats.push({
        chatId: doc.id,
        clientId: chatData.clientId,
        clientName: chatData.clientName || 'Client',
        clientPhoto: chatData.clientPhoto || '/default-client.png',
        bookingId: chatData.bookingId,
        bookingDate: chatData.bookingDate,
        lastMessage: chatData.lastMessage || '',
        lastMessageTime: chatData.lastMessageTime || chatData.updatedAt || new Date(),
        unreadCount: chatData.unreadCount || 0,
        status: chatData.status || 'active',
        caseType: chatData.caseType || 'Legal Consultation'
      });
    });
    
    console.log('🔍 Found', chats.length, 'chats for lawyer in database');
    return { success: true, data: chats };
  } catch (error) {
    console.error('Error getting lawyer chats:', error);
    return { success: false, error: error.message };
  }
};

// Subscribe to lawyer chats in real-time (active and pending only)
export const subscribeToLawyerChats = async (lawyerId, callback) => {
  try {
    if (!lawyerId) {
      console.error('subscribeToLawyerChats: lawyerId is undefined or null');
      return null;
    }
    
    const { db } = await import('../config/firebase');
    const { collection, query, where, orderBy, onSnapshot } = await import('firebase/firestore');
    
    const chatsRef = collection(db, 'chats');
    const q = query(
      chatsRef,
      where('lawyerId', '==', lawyerId),
      where('status', 'in', ['active', 'pending']), // Show both active and pending chats
      orderBy('updatedAt', 'desc')
    );
    
    return onSnapshot(q, (querySnapshot) => {
      const chatMap = new Map(); // Track best chat per client
      
      querySnapshot.forEach((doc) => {
        const chatData = doc.data();
        const clientId = chatData.clientId;
        
        if (!clientId) {
          console.log('⚠️ Chat without clientId:', doc.id);
          return;
        }
        
        const existingChat = chatMap.get(clientId);
        const currentTime = chatData.lastMessageTime || chatData.updatedAt || new Date(0);
        
        if (!existingChat || currentTime > existingChat.time) {
          // Filter out system messages for lawyer view
          let displayMessage = chatData.lastMessage || '';
          if (displayMessage.includes('✅ Booking confirmed!') || 
              displayMessage.includes('Please arrive 10 minutes early') ||
              displayMessage.includes('system')) {
            displayMessage = 'New consultation started';
          }
          
          // Replace or add the better chat
          chatMap.set(clientId, {
            chatId: doc.id,
            clientId: clientId,
            clientName: chatData.clientName || 'Client',
            clientPhoto: chatData.clientPhoto || '/default-client.png',
            bookingId: chatData.bookingId,
            bookingDate: chatData.bookingDate,
            lastMessage: displayMessage,
            lastMessageTime: chatData.lastMessageTime || chatData.updatedAt || new Date(),
            unreadCount: chatData.unreadCount || 0,
            status: chatData.status || 'active',
            caseType: chatData.caseType || 'Legal Consultation',
            time: currentTime
          });
        } else {
          console.log('🔄 Skipping older chat for client:', clientId, 'keeping newer one');
        }
      });
      
      // Convert map to array and sort by time
      const deduplicatedChats = Array.from(chatMap.values())
        .sort((a, b) => b.time - a.time);
      
      console.log('📋 Active/Pending chats:', deduplicatedChats.length, 'unique clients');
      callback(deduplicatedChats);
    });
  } catch (error) {
    console.error('Error subscribing to lawyer chats:', error);
    return null;
  }
};

// Subscribe to completed lawyer chats in real-time
export const subscribeToCompletedLawyerChats = async (lawyerId, callback) => {
  try {
    if (!lawyerId) {
      console.error('subscribeToCompletedLawyerChats: lawyerId is undefined or null');
      return null;
    }
    
    const { db } = await import('../config/firebase');
    const { collection, query, where, orderBy, onSnapshot, getDoc, doc } = await import('firebase/firestore');
    
    const chatsRef = collection(db, 'chats');
    const q = query(
      chatsRef,
      where('lawyerId', '==', lawyerId),
      where('status', '==', 'completed'), // Show only completed chats
      orderBy('updatedAt', 'desc')
    );
    
    return onSnapshot(q, async (querySnapshot) => {
      const chatMap = new Map(); // Track best chat per client
      
      // Process each chat and fetch client profiles
      for (const docSnapshot of querySnapshot.docs) {
        const chatData = docSnapshot.data();
        const clientId = chatData.clientId;
        
        if (!clientId) {
          console.log('⚠️ Completed chat without clientId:', docSnapshot.id);
          continue;
        }
        
        const existingChat = chatMap.get(clientId);
        const currentTime = chatData.lastMessageTime || chatData.updatedAt || new Date(0);
        
        if (!existingChat || currentTime > existingChat.time) {
          // Fetch fresh client profile data
          let clientName = chatData.clientName || 'Client';
          let clientPhoto = chatData.clientPhoto || '/default-client.png';
          
          try {
            const userDoc = await getDoc(doc(db, 'users', clientId));
            if (userDoc.exists()) {
              const userData = userDoc.data();
              clientName = userData.full_name || userData.displayName || userData.name || chatData.clientName || 'Client';
              clientPhoto = userData.profile_picture || userData.photoURL || chatData.clientPhoto || '/default-client.png';
              console.log('✅ Fetched client profile for completed chat:', clientId, 'Name:', clientName);
            }
          } catch (error) {
            console.log('⚠️ Could not fetch client profile for completed chat:', clientId, error.message);
          }
          
          // Filter out system messages for lawyer view
          let displayMessage = chatData.lastMessage || '';
          if (displayMessage.includes('✅ Booking confirmed!') || 
              displayMessage.includes('Please arrive 10 minutes early') ||
              displayMessage.includes('system')) {
            displayMessage = 'New consultation started';
          }
          
          // Replace or add the better chat with enhanced client data
          chatMap.set(clientId, {
            chatId: docSnapshot.id,
            clientId: clientId,
            clientName: clientName,
            clientPhoto: clientPhoto,
            bookingId: chatData.bookingId,
            bookingDate: chatData.bookingDate,
            lastMessage: displayMessage,
            lastMessageTime: chatData.lastMessageTime || chatData.updatedAt || new Date(),
            unreadCount: chatData.unreadCount || 0,
            status: chatData.status || 'completed',
            caseType: chatData.caseType || 'Legal Consultation',
            time: currentTime
          });
        } else {
          console.log('🔄 Skipping older completed chat for client:', clientId, 'keeping newer one');
        }
      }
      
      // Convert map to array and sort by time
      const deduplicatedChats = Array.from(chatMap.values())
        .sort((a, b) => b.time - a.time);
      
      console.log('📋 Completed chats with enhanced profiles:', deduplicatedChats.length, 'unique clients');
      callback(deduplicatedChats);
    });
  } catch (error) {
    console.error('Error subscribing to completed lawyer chats:', error);
    return null;
  }
};

// Get chat data for a specific chat
export const getChatData = async (chatId) => {
  try {
    console.log('🔍 getChatData called with chatId:', chatId);
    
    if (!chatId) {
      console.error('❌ getChatData: chatId is required');
      return { success: false, error: 'Chat ID is required' };
    }
    
    const { db } = await import('../config/firebase');
    const { doc, getDoc } = await import('firebase/firestore');
    
    console.log('🔍 Querying chats collection for chatId:', chatId);
    const chatRef = doc(db, 'chats', chatId);
    const chatDoc = await getDoc(chatRef);
    
    if (chatDoc.exists()) {
      const chatData = chatDoc.data();
      console.log('✅ Chat found:', { chatId, ...chatData });
      return { success: true, data: { id: chatDoc.id, ...chatData } };
    } else {
      console.error('❌ Chat not found for ID:', chatId);
      return { success: false, error: 'Chat not found' };
    }
  } catch (error) {
    console.error('❌ Error getting chat data:', error);
    return { success: false, error: error.message };
  }
};

// Find existing chat by client ID and lawyer ID
export const findExistingChat = async (clientId, lawyerId) => {
  try {
    console.log('🔍 Finding existing chat for client:', clientId, 'and lawyer:', lawyerId);
    
    if (!clientId || !lawyerId) {
      console.error('❌ findExistingChat: clientId and lawyerId are required');
      return { success: false, error: 'Client ID and Lawyer ID are required' };
    }
    
    const { db } = await import('../config/firebase');
    const { collection, query, where, getDocs } = await import('firebase/firestore');
    
    const chatsRef = collection(db, 'chats');
    const q = query(
      chatsRef,
      where('clientId', '==', clientId),
      where('lawyerId', '==', lawyerId)
    );
    
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const existingChat = querySnapshot.docs[0];
      console.log('✅ Existing chat found:', existingChat.id);
      return { 
        success: true, 
        data: { id: existingChat.id, ...existingChat.data() },
        exists: true
      };
    } else {
      console.log('📭 No existing chat found for client:', clientId);
      return { 
        success: true, 
        data: null,
        exists: false
      };
    }
  } catch (error) {
    console.error('❌ Error finding existing chat:', error);
    return { success: false, error: error.message };
  }
};

// Clean up oversized chat documents
export const cleanupOversizedChats = async () => {
  try {
    console.log('🧹 Starting cleanup of oversized chat documents...');
    
    const { db } = await import('../config/firebase');
    const { collection, getDocs, doc, deleteDoc, writeBatch } = await import('firebase/firestore');
    
    const chatsRef = collection(db, 'chats');
    const querySnapshot = await getDocs(chatsRef);
    
    let cleanedCount = 0;
    const batch = writeBatch(db);
    
    for (const chatDoc of querySnapshot.docs) {
      const chatData = chatDoc.data();
      
      // Check if chat document is too large (approximate size check)
      const chatSize = JSON.stringify(chatData).length;
      if (chatSize > 500000) { // 500KB threshold
        console.log(`🗑️ Chat ${chatDoc.id} is oversized (${Math.round(chatSize/1024)}KB), marking for cleanup`);
        
        // Delete the oversized chat document
        batch.delete(doc(db, 'chats', chatDoc.id));
        cleanedCount++;
      }
    }
    
    if (cleanedCount > 0) {
      await batch.commit();
      console.log(`✅ Cleaned up ${cleanedCount} oversized chat documents`);
    } else {
      console.log('✅ No oversized chat documents found');
    }
    
    return { success: true, cleanedCount };
  } catch (error) {
    console.error('❌ Error cleaning up oversized chats:', error);
    return { success: false, error: error.message };
  }
};

// Delete a chat
export const deleteChat = async (chatId) => {
  try {
    if (!chatId) {
      return { success: false, error: 'Chat ID is required' };
    }
    
    console.log('🗑️ Deleting chat:', chatId);
    
    const { db } = await import('../config/firebase');
    const { doc, deleteDoc, collection, getDocs } = await import('firebase/firestore');
    
    // First, delete all messages in the chat subcollection
    try {
      const messagesRef = collection(db, 'chats', chatId, 'messages');
      const messagesSnapshot = await getDocs(messagesRef);
      
      console.log('🗑️ Found', messagesSnapshot.size, 'messages to delete');
      
      const deletePromises = messagesSnapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);
      
      console.log('✅ All messages deleted');
    } catch (messagesError) {
      console.error('❌ Error deleting messages:', messagesError);
      // Continue with chat deletion even if messages deletion fails
    }
    
    // Then delete the chat document
    const chatRef = doc(db, 'chats', chatId);
    await deleteDoc(chatRef);
    
    console.log('✅ Chat deleted successfully');
    
    return { success: true, message: 'Chat and all messages deleted successfully' };
  } catch (error) {
    console.error('Error deleting chat:', error);
    return { success: false, error: error.message };
  }
};

// End a conversation (mark as completed)
export const endConversation = async (chatId) => {
  try {
    if (!chatId) {
      return { success: false, error: 'Chat ID is required' };
    }
    
    const { db } = await import('../config/firebase');
    const { doc, updateDoc } = await import('firebase/firestore');
    
    const chatRef = doc(db, 'chats', chatId);
    await updateDoc(chatRef, {
      status: 'completed',
      updatedAt: new Date()
    });
    
    return { success: true };
  } catch (error) {
    console.error('Error ending conversation:', error);
    return { success: false, error: error.message };
  }
}; 

// Migrate existing pending chats to active status (for legacy chats)
export const migratePendingChats = async () => {
  try {
    console.log('🔄 Starting migration of pending chats to active status...');
    
    const { db } = await import('../config/firebase');
    const { collection, query, where, getDocs, updateDoc, serverTimestamp } = await import('firebase/firestore');
    
    // Find all pending chats
    const pendingChatsQuery = query(
      collection(db, 'chats'),
      where('status', '==', 'pending')
    );
    
    const pendingChatsSnapshot = await getDocs(pendingChatsQuery);
    
    if (pendingChatsSnapshot.empty) {
      console.log('✅ No pending chats found to migrate');
      return { success: true, migratedCount: 0 };
    }
    
    console.log(`🔄 Found ${pendingChatsSnapshot.size} pending chats to migrate`);
    
    // Update all pending chats to active
    const updatePromises = pendingChatsSnapshot.docs.map(doc => 
      updateDoc(doc.ref, { 
        status: 'active',
        updatedAt: serverTimestamp()
      })
    );
    
    await Promise.all(updatePromises);
    
    console.log(`✅ Successfully migrated ${pendingChatsSnapshot.size} chats to active status`);
    
    return { 
      success: true, 
      migratedCount: pendingChatsSnapshot.size 
    };
  } catch (error) {
    console.error('❌ Error migrating pending chats:', error);
    return { success: false, error: error.message };
  }
}; 